import React from "react";
import "./Clustering.css";
import p1 from "./p1.png"
import p2 from "./p2.png"
const Clustering = () => {
  return (
    <div className="clustering-page">
      <header className="clustering-header">
        <h1>NHL Team Style Clustering (5-on-5)</h1>
        <p className="subtitle">
          Using MoneyPuck team-level 5v5 data, Principal Component Analysis (PCA),
          and K-Means clustering to discover playing styles in the 2024 NHL season.
        </p>
      </header>

      {/* DATA & PIPELINE OVERVIEW */}
      <section className="cl-section">
        <p>The goal of this analysis is to determine whether NHL teams exhibit distinct and interpretable 5-on-5 playing styles when viewed through shot quality, shot volume, and pace metrics, rather than simple win-loss outcomes. By clustering teams in a reduced feature space, we aim to identify structural differences in how teams play at even strength.</p>
        <h2>1. Data & Pipeline Overview</h2>
        <div className="cl-two-col">
          <div>
            <h3>Data Source</h3>
            <ul>
              <li>MoneyPuck.com team-level CSV for the 2023-24 NHL season.</li>
              <li>Filtered to <strong>Team Level</strong> rows and the
                  <strong> 5on5</strong> situation only.</li>
              <li>Each row = one <strong>team-season</strong> at 5-on-5 (e.g. CAR-2024).</li>
            </ul>

            <h3>Features Used</h3>
            <p>
              We used a set of 5v5 metrics that describe team strength and style
              rather than just raw totals:
            </p>
            <ul className="cl-chip-list">
              <li>xGoalsPercentage, Corsi%, Fenwick%</li>
              <li>xGoalsFor / xGoalsAgainst</li>
              <li>ShotAttemptsFor / Against, UnblockedAttemptsFor / Against</li>
              <li>Low / Medium / High Danger xG For &amp; Against</li>
              <li>Score &amp; venue-adjusted xG and total shot credit</li>
            </ul>
          </div>

          <div className="cl-card">
            <h3>Analysis Pipeline</h3>
            <ol>
              <li>Load MoneyPuck team-level CSV.</li>
              <li>Filter to <strong>position = "Team Level"</strong> and <strong>situation = "5on5"</strong>.</li>
              <li>Select style-related features and fill missing values.</li>
              <li>Standardize features with <code>StandardScaler</code>.</li>
              <li>Apply <strong>PCA</strong> to reduce to 2 main components.</li>
              <li>Use <strong>Elbow Method</strong> to pick K for K-Means.</li>
              <li>Run <strong>K-Means clustering</strong> (K = 4).</li>
              <li>Visualize team-seasons in the PC1–PC2 plane.</li>
            </ol>
          </div>
        </div>
      </section>

      {/* ELBOW METHOD */}
      <section className="cl-section">
        <h2>2. Choosing the Number of Clusters (Elbow Method)</h2>
        <div className="cl-two-col">
          <div>
            <p>
              We trained K-Means for K = 2 to 10 clusters and plotted the{" "}
              <strong>inertia</strong> (within-cluster sum of squares). Inertia
              always decreases as K increases, but the goal is to find the{" "}
              <strong>&quot;elbow&quot;</strong> where adding more clusters
              yields diminishing returns.
            </p>
            <ul>
              <li>Big drop from K = 2 → 3 → 4.</li>
              <li>Only small improvements after K ≥ 5.</li>
              <li>
                The elbow appears around <strong>K = 3–4</strong>.
              </li>
            </ul>
            <p>
              We chose <strong>K = 4</strong> because it captures rich playing
              style differences without over-segmenting the league.
            </p>

          <p>The elbow method helps choose how many clusters to use by finding the point where adding more clusters stops giving much improvement.
<br/><br/>
We used it to pick a number of clusters that shows real differences between teams without splitting them into too many small, noisy groups.</p>
          </div>
          <div className="cl-img-card">
            {/* Replace src with your real path */}
            <img
              src={p2}
              alt="Elbow plot for K-Means clustering"
            />
            <p className="img-caption">
              Elbow Method: inertia drops rapidly up to K ≈ 4 and then flattens.
            </p>
          </div>
        </div>
      </section>

      {/* PCA EXPLANATION */}
      <section className="cl-section">
        <h2>3. Principal Component Analysis (PCA)</h2>
        <p>
         PCA is used here to reduce many correlated team statistics into a small number of interpretable dimensions, making team similarities easier to visualize and explain. For this dataset:
        </p>
        <ul>
          <li>PC1 + PC2 together explain about <strong>84%</strong> of the variance.</li>
          <li>
            Clustering is performed in the full standardized feature space; PCA is
            used for interpretation and visualization.
          </li>
        </ul>

        <div className="pc-grid">
          <div className="pc-card pc1">
            <h3>PC1 – Team Strength / Territorial Dominance</h3>
            <p>
              PC1 loads <strong>positively</strong> on:
            </p>
            <ul>
              <li>xGoalsPercentage, Corsi%, Fenwick%</li>
              <li>xGoalsFor, ShotAttemptsFor, UnblockedAttemptsFor</li>
              <li>Danger xG For &amp; score-adjusted offensive metrics</li>
            </ul>
            <p>
              and <strong>negatively</strong> on:
            </p>
            <ul>
              <li>ShotsAgainst, ShotAttemptsAgainst, Danger xG Against</li>
              <li>Score-adjusted defensive shot credit against</li>
            </ul>
            <p>
              <strong>Interpretation:</strong> teams far to the right on PC1 are
              strong 5v5 teams that consistently out-shoot and out-chance their
              opponents. Teams far left struggle to drive play.
            </p>
          </div>

          <div className="pc-card pc2">
            <h3>PC2 – Pace / Event Level (Chaos vs Control)</h3>
            <p>
              PC2 loads positively on both offensive and defensive danger xG and
              total shot credit, meaning it increases when games feature:
            </p>
            <ul>
              <li>More high-quality chances For <em>and</em> Against</li>
              <li>Higher tempo and more transitions</li>
              <li>Overall higher event counts</li>
            </ul>
            <p>
              <strong>Interpretation:</strong> high PC2 teams play{" "}
              <strong>fast, high-event &quot;run-and-gun&quot; hockey</strong>;
              low PC2 teams are <strong>low-event, control-oriented</strong> and
              suppress chaos.
            </p>
          </div>
        </div>

        <div className="cl-img-card wide">
          {/* Replace src with your real path */}
          <img
            src={p1}
            alt="PCA scatter plot with K-Means clusters"
          />
          <p className="img-caption">
            PCA projection (PC1 vs PC2) with K-Means clusters. Each point is a
            team-season at 5v5 in 2024.
          </p>
        </div>
      </section>

      {/* CLUSTERS */}
      <section className="cl-section">
        <h2>4. Cluster Results – 2024 5v5 Team Identities</h2>
        <p>
          K-Means with K = 4 groups teams into four distinct playing style
          clusters. Below, each cluster lists its members and a short semantic
          description based on their statistics and PCA positions.
        </p>

        <div className="cluster-grid">
          {/* Cluster 0 */}
          <div className="cluster-card elite">
            <h3>Cluster 0 – Elite Engines</h3>
            <p className="cluster-tag">High PC1 • Medium–High PC2</p>
            <p>
              <strong>Identity:</strong> dominant 5v5 teams that drive play,
              generate heavy offense, and still defend well.
            </p>
            <ul>
              <li>CAR-2024</li>
              <li>COL-2024</li>
              <li>EDM-2024</li>
              <li>FLA-2024</li>
              <li>LAK-2024</li>
              <li>UTA-2024</li>
            </ul>
            <p className="cluster-notes">
              These clubs combine strong shot share, high xGF, and solid shot
              suppression – true analytics darlings.
            </p>
          </div>

          {/* Cluster 1 */}
          <div className="cluster-card rebuild">
            <h3>Cluster 1 – Rebuild / Chaos</h3>
            <p className="cluster-tag">Low PC1 • Medium PC2</p>
            <p>
              <strong>Identity:</strong> weaker 5v5 teams with inconsistent
              structure and more chaotic games. Often rebuilding or flawed
              rosters.
            </p>
            <ul>
              <li>ANA-2024</li>
              <li>BUF-2024</li>
              <li>CBJ-2024</li>
              <li>CHI-2024</li>
              <li>DET-2024</li>
              <li>MTL-2024</li>
              <li>NYR-2024</li>
              <li>SEA-2024</li>
              <li>SJS-2024</li>
            </ul>
            <p className="cluster-notes">
              These teams are underwater in xG and often rely on goaltending,
              special teams, or individual talent to survive.
            </p>
          </div>

          {/* Cluster 2 */}
          <div className="cluster-card solid">
            <h3>Cluster 2 – Strong, Balanced Contenders</h3>
            <p className="cluster-tag">Medium-High PC1 • Medium PC2</p>
            <p>
              <strong>Identity:</strong> good two-way teams with reliable
              structure and positive xG differentials, but not as extreme as the
              elite cluster.
            </p>
            <ul>
              <li>DAL-2024</li>
              <li>NJD-2024</li>
              <li>NSH-2024</li>
              <li>NYI-2024</li>
              <li>PHI-2024</li>
              <li>PIT-2024</li>
              <li>TBL-2024</li>
              <li>VGK-2024</li>
              <li>WPG-2024</li>
              <li>WSH-2024</li>
            </ul>
            <p className="cluster-notes">
              Structured playoff-caliber teams; generally win the 5v5 battle but
              with less extreme dominance than the elite group.
            </p>
          </div>

          {/* Cluster 3 */}
          <div className="cluster-card control">
            <h3>Cluster 3 – Low-Event Controllers</h3>
            <p className="cluster-tag">Medium PC1 • Low PC2</p>
            <p>
              <strong>Identity:</strong> defense-first teams that keep games
              low-event, rely on structure, and suppress dangerous chances.
            </p>
            <ul>
              <li>BOS-2024</li>
              <li>CGY-2024</li>
              <li>MIN-2024</li>
              <li>OTT-2024</li>
              <li>STL-2024</li>
              <li>TOR-2024</li>
              <li>VAN-2024</li>
            </ul>
            <p className="cluster-notes">
              Games involving these teams tend to be slower and tighter;
              breaking them down at 5v5 is difficult, even if the raw offense
              is not always explosive.
            </p>
          </div>
        </div>
      </section>
      <section className="cl-section">
        <h2>5. PCA Loadings – How We Know What PC1 &amp; PC2 Mean</h2>
        <p>
          PCA gives us a <strong>loading</strong> (weight) for each original
          feature on each principal component. Large positive or negative
          weights tell us which stats drive each component. Below is a sample
          of the loading matrix for PC1 and PC2:
        </p>

        <div className="cl-card">
          <table className="pca-table">
            <thead>
              <tr>
                <th>Feature</th>
                <th>PC1 Weight</th>
                <th>PC2 Weight</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>xGoalsPercentage</td>
                <td>0.264</td>
                <td>-0.033</td>
              </tr>
              <tr>
                <td>corsiPercentage</td>
                <td>0.253</td>
                <td>0.060</td>
              </tr>
              <tr>
                <td>fenwickPercentage</td>
                <td>0.265</td>
                <td>0.037</td>
              </tr>
              <tr>
                <td>xGoalsFor</td>
                <td>0.217</td>
                <td>0.266</td>
              </tr>
              <tr>
                <td>shotAttemptsFor</td>
                <td>0.233</td>
                <td>0.176</td>
              </tr>
              <tr>
                <td>shotAttemptsAgainst</td>
                <td>-0.240</td>
                <td>0.097</td>
              </tr>
              <tr>
                <td>highDangerxGoalsAgainst</td>
                <td>-0.133</td>
                <td>0.332</td>
              </tr>
              <tr>
                <td>scoreVenueAdjustedxGoalsFor</td>
                <td>0.221</td>
                <td>0.250</td>
              </tr>
              <tr>
                <td>scoreVenueAdjustedxGoalsAgainst</td>
                <td>-0.206</td>
                <td>0.304</td>
              </tr>
              <tr>
                <td>scoreAdjustedTotalShotCreditFor</td>
                <td>0.224</td>
                <td>0.225</td>
              </tr>
              <tr>
                <td>scoreAdjustedTotalShotCreditAgainst</td>
                <td>-0.217</td>
                <td>0.277</td>
              </tr>
            </tbody>
          </table>
          <p className="pca-note">
            (Values shown are from the PCA model trained on standardized 5v5
            team features for the 2024 season.)
          </p>
        </div>

        <h3 style={{ marginTop: "1rem" }}>How we interpret PC1 and PC2 from this table</h3>
        <ul>
          <li>
            <strong>PC1:</strong> has strong <em>positive</em> weights on
            xGoalsPercentage, Corsi%, Fenwick%, and offensive xG/shot metrics,
            and strong <em>negative</em> weights on shots and xG against. This
            means PC1 increases when a team <strong>drives play, out-shoots, and
            out-chances opponents</strong> and decreases when they get hemmed
            in. We interpret PC1 as{" "}
            <strong>“team strength / territorial dominance.”</strong>
          </li>
          <li>
            <strong>PC2:</strong> has positive weights on both danger xG For
            and danger xG Against, and on total shot credit For and Against.
            That tells us it grows when games have{" "}
            <strong>more high-quality chances at both ends and higher event
            counts</strong>. We interpret PC2 as{" "}
            <strong>“pace / event level (run-and-gun vs. low-event control).”</strong>
          </li>
          <li>
            The combination of the loading signs and magnitudes, plus where
            known teams (e.g. CAR, BOS, EDM, TOR) sit in the PC1–PC2 plot,
            confirms these interpretations in both math and hockey terms.
          </li>
        </ul>
      </section>
      {/* TAKEAWAYS */}
      <section className="cl-section">
        <h2>6. Key Takeaways</h2>
        <ul className="takeaways">
          <li>
            <strong>PCA</strong> reveals that most inter-team variation can be
            summarized by:
            <ul>
              <li>
                <strong>PC1:</strong> overall 5v5 team strength and territorial
                dominance.
              </li>
              <li>
                <strong>PC2:</strong> pace / event level (run-and-gun vs
                low-event control).
              </li>
            </ul>
          </li>
          <li>
            <strong>K-Means (K = 4)</strong> separates teams into interpretable
            playing style clusters: Elite Engines, Rebuild/Chaos, Strong
            Contenders, and Low-Event Controllers.
          </li>
          <li>
            This framework can easily be extended to multiple seasons, special
            teams (PP/PK), or player / line clustering.
          </li>
        </ul>
        <p className="credit">
          Data source: <strong>MoneyPuck.com</strong> – used for non-commercial,
          academic analysis with credit.
        </p>
      </section>
    </div>
  );
};

export default Clustering;
